import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, CheckCircle, AlertCircle, Upload, Download, Loader2 } from 'lucide-react';
import { STATUTS_PRESENCE, getDisplayStatut } from './constants';

const ParticipantCard = ({
  participant,
  onStatutChange,
  onUploadJustificatif,
  onDownloadJustificatif,
  uploadingJustificatif
}) => {
  const statut = participant.statut_presence || 'non_defini';
  // displayStatut tient compte de la règle "absent porté = en attente"
  const displayStatutInfo = getDisplayStatut(participant);
  const StatusIcon = displayStatutInfo.icon;

  return (
    <Card className={`border-2 transition-colors ${displayStatutInfo.color.split(' ')[0]} border-opacity-50`}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-3">
          {/* Ligne 1: Info + Statut */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <span className="font-semibold text-slate-900">
                  {participant.prenom} {participant.nom}
                </span>
                {participant.zone_geographique && (
                  <p className="text-xs text-slate-500">{participant.zone_geographique}</p>
                )}
              </div>
            </div>

            <Select
              value={statut}
              onValueChange={(v) => onStatutChange(participant.id, v)}
            >
              <SelectTrigger className={`w-52 rounded-xl border-2 ${displayStatutInfo.color}`}>
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <StatusIcon className="w-4 h-4" />
                    <span className="font-medium">{displayStatutInfo.label}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUTS_PRESENCE).map(([key, info]) => {
                  const Icon = info.icon;
                  return (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span>{info.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Actions selon le statut */}
          {statut === 'present' && (
            <div className="pl-13 flex items-center gap-3 flex-wrap">
              {participant.signature ? (
                <Badge className="bg-emerald-100 text-emerald-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Signé par le bénéficiaire
                </Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  En attente de signature (espace bénéficiaire)
                </Badge>
              )}
            </div>
          )}

          {statut === 'absent_justifie' && (
            <div className="pl-13 flex items-center gap-3 flex-wrap">
              {participant.justificatif_id ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Justificatif fourni
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDownloadJustificatif(participant.justificatif_id, participant.justificatif_nom || 'justificatif')}
                    className="rounded-xl text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Télécharger
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => onUploadJustificatif(participant.id, e.target.files?.[0])}
                      disabled={uploadingJustificatif === participant.id}
                    />
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="rounded-xl text-amber-600 border-amber-300"
                      asChild
                    >
                      <span>
                        {uploadingJustificatif === participant.id ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-3 h-3 mr-1" />
                        )}
                        {uploadingJustificatif === participant.id ? 'Envoi...' : 'Ajouter justificatif'}
                      </span>
                    </Button>
                  </label>
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Justificatif requis
                  </Badge>
                </div>
              )}
            </div>
          )}

          {statut === 'absent_non_justifie' && (
            <p className="text-xs text-slate-500 pl-13">
              Aucune action requise pour une absence non justifiée
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ParticipantCard;
